import { Stack, Table } from '@mantine/core'
import { z } from 'zod'
import { IconTable } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useCompetition } from '../../../../common/components/header/api'
import { useCompetitionId } from '../../../../common/hooks'
import { Metric, useMetrics, useMetricsData } from '../../../../widgets/api'
import { Fmt, FmtColored } from '../../../../widgets/format'
import { DashboardWidget } from './types'
import {
  ColorSchemaFields,
  ComponentStatisticFields,
  MetricSelect,
  NeutralColorsToggle,
  SubjectFields,
  SubjectSchemaFields,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSection, WidgetSuspense } from './WidgetShell'

const Schema = z.object({
  ...SubjectSchemaFields,
  ...ColorSchemaFields,
  title: z.string().default(''),
  metric_key: z.string().default(''),
  component: z.string().default('percent'),
  statistic: z.string().default('value_ytd'),
})

type Settings = z.infer<typeof Schema>

function Inner({ settings }: { settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const metricKey =
    settings.metric_key ||
    allMetrics.find((m) => m.is_kpi)?.key ||
    allMetrics[0]?.key ||
    ''
  const metric = allMetrics.find((m) => m.key === metricKey)
  if (!metric) return <WidgetEmpty message="No metric available" />
  return <InnerWithMetric metric={metric} settings={settings} />
}

function InnerWithMetric({
  metric,
  settings,
}: {
  metric: Metric
  settings: Settings
}) {
  const competitionId = useCompetitionId()
  const { data: competition } = useCompetition(competitionId)
  const metricKey = metric.key

  const competitionYear = competition?.starts_at
    ? dayjs(competition.starts_at).year()
    : dayjs().year()

  const months = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0')
    return {
      period: `${competitionYear}-${month}-01`,
      label: dayjs(`${competitionYear}-${month}-01`).format('MMMM'),
    }
  })

  const { data: dataPoints } = useMetricsData({
    period_type: 'month',
    period_range: false,
    periods: months.map((m) => m.period),
    subject_type: settings.subject_type,
    subject_ids: [settings.subject_id],
    metrics: months.map(() => metricKey),
    components: months.map(() => settings.component),
    statistics: months.map(() => settings.statistic),
    competition_id: competitionId,
  })

  const valueByPeriod = new Map<string, number>()
  for (const point of dataPoints) valueByPeriod.set(point.period, point.value)

  const title = settings.title || `${metric.short_name || metric.name} YTD`
  const ValueText = settings.use_neutral_colors ? Fmt : FmtColored

  return (
    <WidgetSection title={title} boxed padding="xl">
      <Table
        styles={{
          table: { backgroundColor: 'transparent' },
          td: {
            padding: '10px 0',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Table.Tbody>
          {months.map((m) => {
            const v = valueByPeriod.get(m.period)
            return (
              <Table.Tr key={m.period}>
                <Table.Td style={{ fontSize: '24px', color: '#dee2e6' }}>
                  {m.label}
                </Table.Td>
                <Table.Td
                  style={{
                    textAlign: 'right',
                    fontSize: '24px',
                    color: 'white',
                  }}
                >
                  {v != null ? (
                    <ValueText
                      value={v}
                      metric={metricKey}
                      component={settings.component}
                      statistic={settings.statistic}
                    />
                  ) : (
                    <span style={{ fontSize: '24px', color: '#dee2e6' }}>
                      -
                    </span>
                  )}
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </WidgetSection>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSuspense>
      <Inner settings={settings} />
    </WidgetSuspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <MetricSelect
        kpiOnly
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <ComponentStatisticFields
        component={value.component}
        statistic={value.statistic}
        compatibility={KpiYtdTableWidget.compatibility}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <SubjectFields
        subject_type={value.subject_type}
        subject_id={value.subject_id}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const KpiYtdTableWidget: DashboardWidget<Settings> = {
  type: 'kpi_ytd_table',
  label: 'KPI YTD Table',
  description: 'Monthly YTD values for one metric',
  icon: IconTable,
  minW: 1,
  minH: 3,
  maxW: 6,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  compatibility: {
    actual: ['value', 'value_ytd'],
    target: ['value', 'value_ytd'],
    percent: ['value', 'value_ytd'],
  },
  Component,
  OptionsForm,
}
